import { DatePipe } from '@angular/common';
import { Component, computed, ElementRef, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, RouterLink } from '@angular/router';
import { LeadMessagePreviewComponent } from '../../../shared/components/lead-message-preview/lead-message-preview.component';
import { CrmListFiltersDropdownComponent } from '../../../shared/components/crm-list-filters-dropdown/crm-list-filters-dropdown.component';
import type { CrmListFilterOption } from '../../../shared/components/crm-list-filters-dropdown/crm-list-filters-dropdown.model';
import {
  ApplicantDto,
  ApplicantStatusOption,
  ApplicantsService,
} from '../../../shared/services/applicants.service';
import { CountriesService } from '../../../shared/services/countries.service';
import {
  PlatformUsersService,
  type AssignablePlatformUserDto,
} from '../../../shared/services/platform-users.service';
import {
  AdminButtonComponent,
  AdminEmptyStateComponent,
  AdminTableComponent,
  AdminToastService,
} from '../../../shared/admin';
import { downloadApplicantsExcel } from '../../../shared/utils/applicant-export.util';
import { parseApplicantsExcel } from '../../../shared/utils/applicant-import.util';
import {
  hasApplicantRouteFilters,
  readApplicantRouteFilters,
} from '../../../shared/utils/platform-crm-route-filters.util';
import { displayContactEmail } from '../../../shared/utils/contact-display.util';
import { PermissionService } from '../../permissions/permission.service';

const ASSIGNED_TO_ME_STORAGE_KEY = 'careergrad.applicants.filter.assignedToMe.platform';
const SHOW_DISQUALIFIED_STORAGE_KEY = 'careergrad.applicants.filter.showDisqualified.platform';

@Component({
  selector: 'app-applicants-page',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    RouterLink,
    AdminTableComponent,
    AdminEmptyStateComponent,
    AdminButtonComponent,
    LeadMessagePreviewComponent,
    CrmListFiltersDropdownComponent,
  ],
  templateUrl: './applicants-page.component.html',
  styleUrl: './applicants-page.component.scss',
})
export class ApplicantsPageComponent implements OnInit, OnDestroy {
  private readonly applicantsService = inject(ApplicantsService);
  private readonly countriesService = inject(CountriesService);
  private readonly platformUsersService = inject(PlatformUsersService);
  private readonly route = inject(ActivatedRoute);
  private readonly toast = inject(AdminToastService);
  private readonly permissions = inject(PermissionService);
  private readonly importInput = viewChild<ElementRef<HTMLInputElement>>('importInput');
  private searchTimer: ReturnType<typeof setTimeout> | undefined;

  readonly showImport = computed(() => this.permissions.canShow('applicants', 'import'));
  readonly canImport = computed(() => this.permissions.canExecute('applicants', 'import'));
  readonly showExport = computed(() => this.permissions.canShow('applicants', 'export'));
  readonly canExport = computed(() => this.permissions.canExecute('applicants', 'export'));

  readonly applicants = signal<ApplicantDto[]>([]);
  readonly statuses = signal<ApplicantStatusOption[]>([]);
  readonly assignableUsers = signal<AssignablePlatformUserDto[]>([]);
  readonly destinationOptions = signal<string[]>([]);
  readonly loading = signal(false);
  readonly exporting = signal(false);
  readonly importing = signal(false);

  readonly displayContactEmail = displayContactEmail;
  readonly total = signal(0);
  readonly page = signal(1);
  readonly pageSizeOptions = [10, 20, 50];

  searchInput = '';
  statusId = '';
  statusCodes = '';
  preferredDestination = '';
  createdFrom = '';
  createdTo = '';
  createdToday = false;
  pendingTasksAssignedToMe = false;
  showArchived = false;
  ownerUserId = '';
  showDisqualified = false;
  pageSize = 20;

  readonly hasNextPage = computed(() => this.page() * this.pageSize < this.total());
  readonly pageSummary = computed(() => {
    const total = this.total();
    if (total === 0) return '0 of 0';
    const start = (this.page() - 1) * this.pageSize + 1;
    const end = Math.min(this.page() * this.pageSize, total);
    return `${start}–${end} of ${total}`;
  });
  readonly assignedToFilterOptions = computed<CrmListFilterOption[]>(() => [
    { value: 'me', label: 'Assigned to me' },
    ...this.assignableUsers().map((u) => ({ value: u.userId, label: u.name })),
  ]);
  readonly statusFilterOptions = computed<CrmListFilterOption[]>(() =>
    this.statuses().map((s) => ({ value: s._id, label: s.name })),
  );
  readonly destinationFilterOptions = computed<CrmListFilterOption[]>(() =>
    this.destinationOptions().map((d) => ({ value: d, label: d })),
  );

  ngOnInit(): void {
    this.ownerUserId = this.readAssignedToMePreference() ? 'me' : '';
    this.showDisqualified = this.readShowDisqualifiedPreference();
    this.loadDestinationOptions();
    this.loadAssignableUsers();
    this.applicantsService.listStatuses().subscribe({
      next: (items) => {
        this.statuses.set(items);
        if (hasApplicantRouteFilters(this.route.snapshot.queryParamMap)) {
          this.applyRouteFilters(this.route.snapshot.queryParamMap, items);
        }
        this.refresh();
      },
    });

    this.route.queryParamMap.subscribe((params) => {
      const items = this.statuses();
      if (items.length === 0 || !hasApplicantRouteFilters(params)) {
        return;
      }
      const previous = this.snapshotApplicantFilters();
      this.applyRouteFilters(params, items);
      if (this.applicantFiltersChanged(previous)) {
        this.page.set(1);
        this.refresh();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  onSearchChange(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.page.set(1);
      this.refresh();
    }, 300);
  }

  onFilterChange(): void {
    this.persistAssignedToMePreference();
    this.persistShowDisqualifiedPreference();
    this.page.set(1);
    this.refresh();
  }

  onAssignedToFilterChange(value: string): void {
    this.ownerUserId = value;
    this.onFilterChange();
  }

  onStatusFilterChange(value: string): void {
    this.statusId = value;
    this.onFilterChange();
  }

  onDestinationFilterChange(value: string): void {
    this.preferredDestination = value;
    this.onFilterChange();
  }

  onFiltersClear(): void {
    this.ownerUserId = '';
    this.statusId = '';
    this.statusCodes = '';
    this.preferredDestination = '';
    this.onFilterChange();
  }

  onAssignedToMeToggle(checked: boolean): void {
    this.ownerUserId = checked ? 'me' : '';
    this.onFilterChange();
  }

  goToPage(next: number): void {
    this.page.set(next);
    this.refresh();
  }

  openImportPicker(): void {
    this.importInput()?.nativeElement.click();
  }

  async onImportFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || this.importing()) return;

    this.importing.set(true);
    try {
      const rows = await parseApplicantsExcel(file);
      if (rows.length === 0) {
        this.toast.error('No applicant rows found in the file.');
        this.importing.set(false);
        return;
      }

      this.applicantsService.importPlatform(rows).subscribe({
        next: (result) => {
          const summary = `Import complete: ${result.created} applicants created, ${result.updated} applicants updated, ${result.leadsCreated} leads created, ${result.leadsUpdated} leads updated, ${result.skipped} skipped.`;
          if (result.errors.length > 0) {
            this.toast.error(`${summary} ${result.errors.length} row issue(s).`);
          } else {
            this.toast.success(summary);
          }
          this.importing.set(false);
          this.refresh();
        },
        error: () => {
          this.toast.error('Import failed. Please check the file and try again.');
          this.importing.set(false);
        },
      });
    } catch {
      this.toast.error('Could not read the Excel file.');
      this.importing.set(false);
    }
  }

  exportApplicants(): void {
    if (this.exporting()) return;
    this.exporting.set(true);
    this.applicantsService
      .exportPlatform({
        search: this.searchInput,
        statusId: this.statusCodes ? undefined : this.statusId || undefined,
        statusCodes: this.statusCodes || undefined,
        preferredDestination: this.preferredDestination || undefined,
        createdFrom: this.createdToday ? undefined : this.createdFrom || undefined,
        createdTo: this.createdToday ? undefined : this.createdTo || undefined,
        createdToday: this.createdToday || undefined,
        pendingTasksAssignedToMe: this.pendingTasksAssignedToMe || undefined,
        archiveFilter: this.showArchived ? 'archived' : 'active',
        ownerUserId: this.ownerUserId || undefined,
        showDisqualified: this.showDisqualified || undefined,
      })
      .subscribe({
        next: (rows) => {
          if (rows.length === 0) {
            this.toast.error('No applicants to export.');
            this.exporting.set(false);
            return;
          }
          const stamp = new Date().toISOString().slice(0, 10);
          downloadApplicantsExcel(rows, `enrolled-applicants-${stamp}.xlsx`);
          this.toast.success(`Exported ${rows.length} applicant${rows.length === 1 ? '' : 's'}.`);
          this.exporting.set(false);
        },
        error: () => {
          this.toast.error('Export failed. Please try again.');
          this.exporting.set(false);
        },
      });
  }

  refresh(): void {
    this.loading.set(true);
    this.applicantsService
      .listPlatform({
        page: this.page(),
        pageSize: this.pageSize,
        search: this.searchInput,
        statusId: this.statusCodes ? undefined : this.statusId || undefined,
        statusCodes: this.statusCodes || undefined,
        preferredDestination: this.preferredDestination || undefined,
        createdFrom: this.createdToday ? undefined : this.createdFrom || undefined,
        createdTo: this.createdToday ? undefined : this.createdTo || undefined,
        createdToday: this.createdToday || undefined,
        pendingTasksAssignedToMe: this.pendingTasksAssignedToMe || undefined,
        archiveFilter: this.showArchived ? 'archived' : 'active',
        ownerUserId: this.ownerUserId || undefined,
        showDisqualified: this.showDisqualified || undefined,
      })
      .subscribe({
        next: (result) => {
          this.applicants.set(result.items);
          this.total.set(result.total);
          this.page.set(result.page);
          this.loading.set(false);
        },
        error: () => {
          this.applicants.set([]);
          this.total.set(0);
          this.loading.set(false);
        },
      });
  }

  private loadDestinationOptions(): void {
    this.countriesService.listPublicCountries().subscribe({
      next: (list) => {
        const names = list.map((c) => c.name?.trim()).filter((v): v is string => Boolean(v));
        this.destinationOptions.set(Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)));
      },
      error: () => this.destinationOptions.set([]),
    });
  }

  private loadAssignableUsers(): void {
    this.platformUsersService.listAssignable().subscribe({
      next: (users) => {
        this.assignableUsers.set([...users].sort((a, b) => a.name.localeCompare(b.name)));
      },
      error: () => this.assignableUsers.set([]),
    });
  }

  private readAssignedToMePreference(): boolean {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    return localStorage.getItem(ASSIGNED_TO_ME_STORAGE_KEY) === 'true';
  }

  private persistAssignedToMePreference(): void {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(ASSIGNED_TO_ME_STORAGE_KEY, String(this.ownerUserId === 'me'));
  }

  private readShowDisqualifiedPreference(): boolean {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    return localStorage.getItem(SHOW_DISQUALIFIED_STORAGE_KEY) === 'true';
  }

  private persistShowDisqualifiedPreference(): void {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(SHOW_DISQUALIFIED_STORAGE_KEY, String(this.showDisqualified));
  }

  private applyRouteFilters(params: ParamMap, items: ApplicantStatusOption[]): void {
    const filters = readApplicantRouteFilters(params, items);
    this.statusId = filters.statusId;
    this.statusCodes = filters.statusCodes;
    this.createdFrom = filters.createdFrom;
    this.createdTo = filters.createdTo;
    this.createdToday = filters.createdToday;
    this.pendingTasksAssignedToMe = filters.pendingTasksAssignedToMe;
    this.showDisqualified = filters.showDisqualified;
  }

  private snapshotApplicantFilters() {
    return {
      statusId: this.statusId,
      statusCodes: this.statusCodes,
      createdFrom: this.createdFrom,
      createdTo: this.createdTo,
      createdToday: this.createdToday,
      pendingTasksAssignedToMe: this.pendingTasksAssignedToMe,
      showDisqualified: this.showDisqualified,
    };
  }

  private applicantFiltersChanged(previous: ReturnType<typeof this.snapshotApplicantFilters>): boolean {
    const current = this.snapshotApplicantFilters();
    return (Object.keys(current) as (keyof typeof current)[]).some((key) => current[key] !== previous[key]);
  }
}
